import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { useApolloClient } from '@apollo/client/react';
import {
  Department,
  DepartmentsSearchListDocument,
  DepartmentsSearchListQuery,
  DepartmentsSearchListQueryVariables,
  FilterGroupingEnum,
  SortDirectionEnum,
} from 'graphql-common';
import PaginatedList, {
  PaginatedListMetadata,
} from '@lib/components/PaginatedList/PaginatedList';
import Chip from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';

type Props = {
  userId: string;
};

export default function UserDepartmentsList({ userId }: Props) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const fetchDepartments = useCallback(
    (options: { variables: DepartmentsSearchListQueryVariables }) =>
      client.query<
        DepartmentsSearchListQuery,
        DepartmentsSearchListQueryVariables
      >({
        query: DepartmentsSearchListDocument,
        variables: options.variables,
        fetchPolicy: 'no-cache',
      }),
    [client],
  );

  const onView = (department: Department) => {
    if (department?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: department.id },
        }),
      );
    }
  };

  return (
    <PaginatedList<
      Department,
      DepartmentsSearchListQuery,
      DepartmentsSearchListQueryVariables
    >
      query={fetchDepartments}
      variables={{
        sorts: { name: SortDirectionEnum.Asc },
        filters: [
          {
            usersId: {
              grouping: FilterGroupingEnum.And,
              predicate: { eq: userId },
            },
          },
        ],
      }}
      pageSize={+PER_PAGE_KEY_DEFAULT}
      getCollection={(data) =>
        _get(data, 'data.collection', []) as Department[]
      }
      getMetadata={(data) =>
        _get(data, 'data.metadata') as PaginatedListMetadata | undefined
      }
      getItemKey={(item) => item.id}
      renderItem={(item) => (
        <Chip
          type={colorToChipType(item.color)}
          label={item.name}
          leftIcon="circle"
          onClick={() => onView(item)}
        />
      )}
    />
  );
}
